// src/modules/app-views/crm/pages/Init/components/BoardsGroupTitle.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Input, Badge } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useDragHandle } from './DraggableCollapseGroup';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BoardsGroupTitle = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { group, boardCount, editingKey, setEditingKey, onDelete, onUpdateTitle, canManage } = props;
  const dragHandle = useDragHandle();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const groupUid = group?.uid || group?._id;
  const isEditing = editingKey === groupUid;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [tempTitle, setTempTitle] = useState(group?.label || '');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleStartEditing = e => {
    e.stopPropagation();
    setTempTitle(group?.label || '');
    setEditingKey(groupUid);
  };

  const handleConfirmEdit = e => {
    e.stopPropagation();
    if (tempTitle.trim()) {
      onUpdateTitle(groupUid, tempTitle.trim());
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleConfirmEdit(e);
    if (e.key === 'Escape') setEditingKey(null);
  };

  const handleDelete = e => {
    e.stopPropagation();
    onDelete(groupUid);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (isEditing) {
    return (
      <Input
        autoFocus
        size="small"
        value={tempTitle}
        onChange={e => setTempTitle(e.target.value)}
        onBlur={handleConfirmEdit}
        onKeyDown={handleKeyDown}
        onClick={e => e.stopPropagation()}
        style={{ width: 300 }}
        placeholder={translateX('group_name')}
      />
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="d-flex align-items-center">
        <span className="font-weight-semibold">{group?.label}</span>
        <Badge count={boardCount} showZero style={{ marginLeft: 8, backgroundColor: '#999' }} />
      </div>
      <div className="d-flex align-items-center" style={{ gap: 18 }}>
        {canManage && (
          <EditOutlined
            className="text-muted"
            style={{ fontSize: 14, cursor: 'pointer' }}
            onClick={handleStartEditing}
          />
        )}
        {canManage && (
          <DeleteOutlined
            className="text-muted"
            style={{ fontSize: 14, cursor: 'pointer' }}
            onClick={handleDelete}
          />
        )}

        {dragHandle}
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BoardsGroupTitle;
